import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";

import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import ReportRevisionNotice from "../../../../Components/v2/Accounting/ReportRevisionNotice";
import Modal from "../../../../Components/v2/Modal";
import FormDate from "../../../../Components/v2/Form/FormDate";
import TableSkeleton from "../../../../Components/TableSkeleton";
import ErrorStateTable from "../../../../Components/ErrorStateTable";

import {
  cancelAPIRequest,
  convertToTitleCase,
  getAccountingFileName,
} from "../../../../Utils";
import {
  clearExportBalanceSheet,
  requestExportBalanceSheet,
  requestListBalanceSheet,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
} from "../../../../Context";
import { EXPORTS } from "../../../../Constants/accountingReport";
import { splitLiabilities } from "../../../../Utils/balanceSheet";
import {
  wibEndOfDay,
  wibMonthRange,
  wibToday,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const AccountingBalanceSheet = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthState();

  const [date, setDate] = useState(wibToday);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [showDownloadErrorModal, setShowDownloadErrorModal] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const accountingDispatch = useAccountingDispatch();

  const {
    request,
    balanceSheets: storedBalanceSheets,
    balanceSheetsLoading,
    balanceSheetsError,
    balanceSheetExport,
    balanceSheetExportLoading,
    balanceSheetExportError,
  } = useAccountingState();

  // With the date cleared nothing is requested, so the store still holds the
  // previous date's report. Show nothing rather than present it under an empty
  // picker.
  const balanceSheets = date ? storedBalanceSheets : null;

  // --- Derived totals ---
  const calculateTotal = (category) =>
    (balanceSheets?.items ?? [])
      .filter((item) => item?.category === category)
      .reduce((sum, item) => sum + item.total, 0);

  const currentAssetsTotal = calculateTotal("CURRENT_ASSETS");
  const fixedAssetsTotal = calculateTotal("FIXED_ASSETS");
  const capitalsTotal = calculateTotal("CAPITAL");
  const liabilities = splitLiabilities(balanceSheets?.items);

  const getByCategory = (category) =>
    (balanceSheets?.items ?? []).filter((item) => item?.category === category);

  const currentAssetsData = getByCategory("CURRENT_ASSETS");
  const fixedAssetsData = getByCategory("FIXED_ASSETS");
  const capitalsData = getByCategory("CAPITAL");

  // Cash/Bank accounts below zero are reported under current Liabilities.
  // Their names carry a " (saldo kredit)" suffix that the banner leaves out.
  const overdraftIDs = balanceSheets?.overdraftAccounts ?? [];
  const overdraftNames = liabilities.currentAccounts
    .filter((account) => account.overdraft || overdraftIDs.includes(account.id))
    .map((account) => {
      const name = (account.name || "").replace(/ \(saldo kredit\)$/, "");
      return account.code ? `${account.code} - ${name}` : name;
    });
  const overdraftList = (() => {
    try {
      return new Intl.ListFormat(i18n.language || "id", {
        style: "long",
        type: "conjunction",
      }).format(overdraftNames);
    } catch (error) {
      return overdraftNames.join(", ");
    }
  })();

  // --- Effects ---
  useEffect(() => {
    return () => {
      if (request) cancelAPIRequest(request);
      clearExportBalanceSheet(accountingDispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user.mainBusiness?.id || !date) return;
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.mainBusiness?.id, date]);

  useEffect(() => {
    if (balanceSheetExport?.key) {
      downloadFile(balanceSheetExport.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSheetExport]);

  useEffect(() => {
    if (balanceSheetExportError) {
      setShowExportError(true);
    }
  }, [balanceSheetExportError]);

  // --- Handlers ---
  const onLoad = () => {
    if (request) cancelAPIRequest(request);
    if (user.mainBusiness?.id && date) {
      requestListBalanceSheet(accountingDispatch, {
        businessID: user.mainBusiness.id,
        date: wibEndOfDay(date),
      });
    }
  };

  const handleExport = useCallback(
    (exportType) => {
      if (!date) return;
      if (user.mainBusiness?.id) {
        requestExportBalanceSheet(accountingDispatch, {
          businessID: user.mainBusiness.id,
          date: wibEndOfDay(date),
          exportType,
        });
      }
    },
    [accountingDispatch, date, user.mainBusiness?.id],
  );

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const req = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { onProgress: onDownloadProgress },
      });
      downloadRef.current = req;
      const result = await req.result;
      const fileName = getAccountingFileName(key);
      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearExportBalanceSheet(accountingDispatch);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
      setShowDownloadErrorModal(true);
    }
  };

  const exportActionItems = EXPORTS.map((item) => ({
    name: t(item.label),
    onClick: () => handleExport(item.value),
  }));

  // --- Table helpers ---
  const dateParams = date
    ? `&startDate=${wibMonthRange(date).startDate}&endDate=${wibMonthRange(date).endDate}`
    : "";

  // FIXED_ASSETS groups every non-current asset (fixed assets, depreciation,
  // other assets), so the section reads "Aset Tidak Lancar" per SAK EP.
  const sectionLabel = (categoryName) =>
    t(
      categoryName === "FIXED_ASSETS"
        ? "Non-current Assets"
        : convertToTitleCase(categoryName),
    );

  // `label`, `totalLabel` and `accounts` let the liability blocks reuse this
  // layout for accounts that are split out of one category.
  const TableSection = ({
    categoryName,
    totalAmount,
    label,
    totalLabel,
    accounts: accountsProp,
  }) => {
    const accounts =
      accountsProp ??
      (balanceSheets?.items ?? [])
        .filter((item) => item?.category === categoryName)
        .flatMap((item) => item.accounts || []);

    return (
      <>
        <tr className="bg-gray-50">
          <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left">
            {label ?? sectionLabel(categoryName)}
          </th>
          <th className="px-4 py-2 font-medium text-sm text-right text-gray-900 whitespace-nowrap">
            {t("Amount")}
          </th>
        </tr>
        {accounts.map((account, index) => {
          const { id, code, name, total, overdraft } = account;
          return (
            <tr key={index}>
              <td className="pl-8 pr-4 py-2 text-sm text-gray-900 break-words min-w-0">
                {code ? (
                  <a
                    href={`/v2/accounting/transactions?accountId=${id}${dateParams}`}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {`${code} - ${name}`}
                  </a>
                ) : (
                  <span>{name}</span>
                )}
                {overdraft && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 whitespace-nowrap">
                    {t("reports.balanceSheets.overdraft.chip")}
                  </span>
                )}
              </td>
              <td className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap">
                <NumericFormat
                  displayType="text"
                  value={total}
                  thousandSeparator="."
                  decimalSeparator=","
                  decimalScale={2}
                  prefix="Rp"
                />
              </td>
            </tr>
          );
        })}
        <tr>
          <td className="px-4 py-2 text-sm text-left text-gray-900">
            {totalLabel ?? `Total ${sectionLabel(categoryName)}`}
          </td>
          <td className="px-4 py-2 text-sm text-right text-gray-900 whitespace-nowrap font-medium">
            <NumericFormat
              displayType="text"
              value={totalAmount}
              thousandSeparator="."
              decimalSeparator=","
              decimalScale={2}
              prefix="Rp"
            />
          </td>
        </tr>
      </>
    );
  };

  const EmptyTableSection = ({ categoryName, totalAmount }) => (
    <tr className="bg-gray-50">
      <th
        className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap"
        colSpan={2}
      >
        {`Total ${sectionLabel(categoryName)}`}
      </th>
      <td className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
        <NumericFormat
          displayType="text"
          value={totalAmount}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="Rp"
        />
      </td>
    </tr>
  );

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="Balance Sheet"
          dropDownAction={{
            label: t("Export"),
            icon: <DownloadIcon className="h-5 w-5" />,
            onClick: () => handleExport("pdf"),
            items: exportActionItems,
            disabled: balanceSheetExportLoading,
          }}
        />
        {/* As-of report: no startDate, the range runs from the start of the book. */}
        <ReportRevisionNotice
          businessID={user.mainBusiness?.id}
          endDate={date}
          className="border-b border-gray-200"
        />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          {/* Date filter */}
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormDate
              selected={date}
              onChange={(val) => setDate(val)}
              disabled={balanceSheetsLoading}
              onClear={() => setDate(null)}
            />
          </div>

          {/* Grouped table */}
          {balanceSheetsLoading ? (
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <TableSkeleton rows={6} columns={2} />
              </table>
            </div>
          ) : (balanceSheets?.items ?? []).length > 0 ? (
            <>
              {balanceSheets?.balanced === false && (
                <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <span className="font-medium">
                    {t("balanceSheet.notBalanced", "Neraca tidak seimbang")}
                  </span>
                  <span>
                    {t("balanceSheet.difference", "Selisih")}:{" "}
                    <NumericFormat
                      displayType="text"
                      value={balanceSheets?.difference ?? 0}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="Rp"
                    />
                  </span>
                </div>
              )}
              {overdraftNames.length > 0 && (
                <div
                  role="status"
                  className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                >
                  <p className="font-medium">
                    {t("reports.balanceSheets.overdraft.title")}
                  </p>
                  <p className="mt-1">
                    {t("reports.balanceSheets.overdraft.body", {
                      accounts: overdraftList,
                      interpolation: { escapeValue: false },
                    })}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Assets column */}
              <div className="overflow-x-auto">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <tbody className="divide-y divide-gray-200 bg-white">
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 text-left">
                          {t("Assets")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left" />
                      </tr>
                      {currentAssetsData.length > 0 ? (
                        <TableSection
                          categoryName="CURRENT_ASSETS"
                          totalAmount={currentAssetsTotal}
                        />
                      ) : (
                        <EmptyTableSection
                          categoryName="CURRENT_ASSETS"
                          totalAmount={currentAssetsTotal}
                        />
                      )}
                      {fixedAssetsData.length > 0 ? (
                        <TableSection
                          categoryName="FIXED_ASSETS"
                          totalAmount={fixedAssetsTotal}
                        />
                      ) : (
                        <EmptyTableSection
                          categoryName="FIXED_ASSETS"
                          totalAmount={fixedAssetsTotal}
                        />
                      )}
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 text-left">
                          {t("Total Assets")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
                          <NumericFormat
                            displayType="text"
                            value={balanceSheets?.totalAsset ?? 0}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            prefix="Rp"
                          />
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Liabilities and Equity column */}
              <div className="overflow-x-auto">
                <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
                  <table className="min-w-full divide-y divide-gray-300">
                    <tbody className="divide-y divide-gray-200 bg-white">
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 text-left">
                          {t("Liabilities and Equity")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-left" />
                      </tr>
                      <TableSection
                        label={t("reports.balanceSheets.currentLiabilities")}
                        totalLabel={t(
                          "reports.balanceSheets.totalCurrentLiabilities",
                        )}
                        accounts={liabilities.currentAccounts}
                        totalAmount={liabilities.currentTotal}
                      />
                      <TableSection
                        label={t("reports.balanceSheets.longTermLiabilities")}
                        totalLabel={t(
                          "reports.balanceSheets.totalLongTermLiabilities",
                        )}
                        accounts={liabilities.longTermAccounts}
                        totalAmount={liabilities.longTermTotal}
                      />
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 text-left">
                          {t("reports.balanceSheets.totalLiabilities")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
                          <NumericFormat
                            displayType="text"
                            value={liabilities.total}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            prefix="Rp"
                          />
                        </th>
                      </tr>
                      {capitalsData.length > 0 ? (
                        <TableSection
                          categoryName="CAPITAL"
                          totalAmount={capitalsTotal}
                        />
                      ) : (
                        <EmptyTableSection
                          categoryName="CAPITAL"
                          totalAmount={capitalsTotal}
                        />
                      )}
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 text-left">
                          {t("Total Liabilities and Equity")}
                        </th>
                        <th className="px-4 py-2 font-medium text-sm text-gray-900 whitespace-nowrap text-right">
                          <NumericFormat
                            displayType="text"
                            value={balanceSheets?.totalLiability ?? 0}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            prefix="Rp"
                          />
                        </th>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </>
          ) : (
            <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-300">
                <tbody className="divide-y divide-gray-200 bg-white">
                  <ErrorStateTable
                    otherError={balanceSheetsError}
                    onRetry={onLoad}
                  />
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Download progress modal */}
        <Modal
          title="Download File"
          open={downloading}
          onClose={() => {
            if (downloadRef.current) {
              downloadRef.current.cancel();
            }
            setDownloading(false);
          }}
          footer={
            <div className="w-full">
              <p className="text-xs text-slate-500 mb-1">{t("Downloading")}</p>
              <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div
                  className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(4, Math.round(downloadProgress * 100))}%`,
                  }}
                >
                  {Math.round(downloadProgress * 100)}%
                </div>
              </div>
            </div>
          }
        >
          <div className="flex justify-center py-6">
            <DocumentIcon className="h-24 w-24 text-slate-400" />
          </div>
        </Modal>

        {/* Error modals */}
        {downloadDocError && (
          <ErrorDialog
            open={showDownloadErrorModal}
            error={downloadDocError}
            onClose={() => {
              setShowDownloadErrorModal(false);
              setDownloadDocError(null);
            }}
          />
        )}
        {balanceSheetExportError && (
          <ErrorDialog
            open={showExportError}
            error={balanceSheetExportError}
            onClose={() => {
              setShowExportError(false);
              clearExportBalanceSheet(accountingDispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingBalanceSheet;
